import React, { useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PersonIcon from "@mui/icons-material/Person";
import ElectricMopedIcon from '@mui/icons-material/ElectricMoped';
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import "./style.css";
import { ZyppLiteApi } from "../../../API/Zypp-Lite-Api/LiteApi";

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  downloadFn: () => Promise<Blob>;
  fileName: string;
}

const Reports: React.FC = () => {
  const [loadingReport, setLoadingReport] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const reportCards: ReportCard[] = [
    {
      id: "user-detail",
      title: "Rider Details Report",
      description: "Download complete user details report in Excel format.",
      icon: <PersonIcon sx={{ fontSize: 48, color: "#06B681" }} />,
      downloadFn: () => ZyppLiteApi.api.downloadUserDetailReport(),
      fileName: "User_Details_Report.xlsx",
    },
    {
      id: "vehicle-detail",
      title: "EV Details Report",
      description: "Download complete vehicle details list in Excel format.",
      icon: <ElectricMopedIcon sx={{ fontSize: 48, color: "#06B681" }} />,
      downloadFn: () => ZyppLiteApi.api.downloadVehicleDetailReport(),
      fileName: "Vehicle_Details_Report.xlsx",
    },
    {
      id: "rfa-vehicles",
      title: "Ready for Allotment Report",
      description:"Download list of vehicles which are Ready For Allotment",
      icon: <AssignmentTurnedInIcon sx={{ fontSize: 48, color: "#06B681" }} />,
      downloadFn: () => ZyppLiteApi.api.downloadRfaVehiclesReport(),
      fileName: "Ready_For_Allotment_Report.xlsx",
    },
    {
      id: "ride-end-vehicles",
      title: "Ride Ended EVs Report",
      description: "Download list of EVs which have been Ride Ended.",
      icon: <StopCircleIcon sx={{ fontSize: 48, color: "#06B681" }} />,
      downloadFn: () => ZyppLiteApi.api.downloadRideEndVehiclesReport(),
      fileName: "Ride_Ended_Vehicles_Report.xlsx",
    },
     {
      id: "transaction-history",
      title: "Transaction History Report",
      description: "",
      icon: <ReceiptLongIcon sx={{ fontSize: 48, color: "#06B681" }} />,
      downloadFn: () => ZyppLiteApi.api.downloadTransactionHistoryReport(),
      fileName: "Transaction_History_Report.xlsx",
    },
  ];

const handleDownload = async (report: ReportCard) => {
  setLoadingReport(report.id);

  try {
    const blob = await report.downloadFn(); 

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = report.fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    setSnackbarMessage(`${report.title} downloaded successfully!`);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  } catch (error) {
    console.error("Download error:", error);
    setSnackbarMessage("Failed to download report.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  } finally {
    setLoadingReport(null);
  }
};
  return (
    <div className="p-10">
      <div className="w-full flex bg-white min-h-80 box-shadow rounded-xl p-4">
        <div className="w-full">
          <div className="flex ml-4 mb-4">
            <h2 className="text-center ml-2 mx-1 mt-2 font-semibold text-2xl text-black pb-2">
              Reports
            </h2>
          </div>
          <hr className="my-4" />

          <Grid container spacing={3} sx={{ p: 2 }}>
            {reportCards.map((report) => (
              <Grid item xs={12} sm={6} md={3} key={report.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 2,
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                    borderRadius: "12px",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    {report.icon}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#263238",
                      }}
                    >
                      {report.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#455A64",
                      }}
                    >
                      {report.description}
                    </Typography>
                  </CardContent>
                  <Button
                      variant="contained"
                      startIcon={
                        loadingReport === report.id ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      onClick={() => handleDownload(report)}
                      disabled={loadingReport === report.id}
                      sx={{
                        backgroundColor: "#06B681",
                        color: "#fff",
                        borderRadius: "40px",
                        textTransform: "none",
                        px: 3,
                        mb: 2,
                        "&:hover": {
                          backgroundColor: "#05a070",
                        },
                       
                      }}
                    >
                      {loadingReport === report.id
                        ? "Downloading..."
                        : "Download Excel"}
                    </Button>
                </Card>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Reports;
